import React, { useState } from "react";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import useCampaignService from "../../services/api/campaignService";
import useCustomerService from "../../services/api/customerService";
import { enums } from "../../constants/enum";
import { customerUpdateValidationSchema } from "../../schemas/customer";

import { EditIcon } from "../../assets/icons";
import { Button, Loading, PageTitle, Section } from "../../components";
import {
  DateInput,
  Radio,
  SelectInput,
  TextInput,
} from "../../components/forms";
import cities from "../../assets/data/cities.json";
import "./CustomersPage.scss";

function AddCustomerPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const { customerId } = useParams();
  const {
    getCustomerById,
    updateCustomer,
    queryKey: getCustomerByIdQueryKey,
  } = useCustomerService();

  const mutation = useMutation({
    mutationFn: updateCustomer,
  });

  const { getCampaignsWithoutPagination, queryKey } = useCampaignService();
  const { data: campaigns = [] } = useQuery({
    queryKey: [queryKey, { page: "*", limit: "*" }],
    queryFn: getCampaignsWithoutPagination,
  });

  const {
    data: customerData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [getCustomerByIdQueryKey, customerId],
    queryFn: () => getCustomerById(customerId),
    enabled: !!customerId,
  });

  if (isLoading) return <Loading variant="fullScreen" />;
  if (error) {
    toast.error(
      error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
    );
    return navigate(-1, { replace: true });
  }
  const initialValues = {
    title: customerData?.customer?.title,
    name: customerData?.customer?.name,
    dob: customerData?.customer?.dob.split("T")[0],
    mobileNumber: customerData?.customer?.mobileNumber.replace(0, ""),
    addressLine1: customerData?.customer?.addressLine1,
    addressLine2: customerData?.customer?.addressLine2,
    city: customerData?.customer?.city,
    type: customerData?.customer?.type,
    ...(customerData?.customer?.campaignId?._id
      ? { campaignId: customerData?.customer?.campaignId._id }
      : {}),
  };

  const handleCustomerUpdate = (values) => {
    const payload = {
      title: values.title,
      name: values.name,
      dob: values.dob,
      mobileNumber: `0${values.mobileNumber}`,
      addressLine1: values.addressLine1,
      addressLine2: values.addressLine2,
      city: values.city,
    };
    mutation.mutate(
      {
        id: customerId,
        payload,
      },
      {
        onSuccess: async (payload) => {
          await queryClient.resetQueries({
            queryKey: [getCustomerByIdQueryKey],
          });
          toast.success(payload?.message);
          navigate(-1, { replace: true });
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.payload?.message ||
              enums.DEFAULT_ERROR_MESSAGE,
          );
        },
        onSettled: () => {
          setIsSaving(false);
        },
      },
    );
  };
  return (
    <div className="page add-customer-page">
      <PageTitle text="Edit New Customer" prev="/customers" />

      <Section className="customer-form">
        <Formik
          initialValues={initialValues}
          onSubmit={handleCustomerUpdate}
          validationSchema={customerUpdateValidationSchema}
        >
          {({ values }) => (
            <Form>
              <div className="input-group">
                <div className="radio-group">
                  <Radio
                    name="title"
                    value={enums.CUSTOMER_TITLES.MR}
                    label="Mr."
                  />
                  <Radio
                    name="title"
                    value={enums.CUSTOMER_TITLES.MRS}
                    label="Mrs."
                  />
                  <Radio
                    name="title"
                    value={enums.CUSTOMER_TITLES.REVEREND}
                    label="Reverent"
                  />
                </div>

                <TextInput label="Name" name="name" placeholder="Type here" />
              </div>

              <div className="input-group">
                <DateInput label="Birthday" name="dob" />
                <TextInput
                  label="Phone"
                  name="mobileNumber"
                  placeholder="Type here"
                  prefix="+94"
                />
              </div>

              <TextInput
                label="Address 1"
                name="addressLine1"
                placeholder="Type here"
              />
              <TextInput
                label="Address 2"
                name="addressLine2"
                placeholder="Type here"
              />

              <TextInput
                label="City"
                name="city"
                isDataListed
                dataListItems={cities}
                placeholder="Type here"
              />

              <SelectInput
                disabled
                label="Customer type"
                name="type"
                values={[
                  {
                    label: enums.CUSTOMER_TYPES.NORMAL,
                    value: enums.CUSTOMER_TYPES.NORMAL,
                  },
                  {
                    label: enums.CUSTOMER_TYPES.EYE_CAMP,
                    value: enums.CUSTOMER_TYPES.EYE_CAMP,
                  },
                ]}
              />
              {values.type === enums.CUSTOMER_TYPES.EYE_CAMP ? (
                <SelectInput
                  label="Campaign Details"
                  name="campaignId"
                  values={[
                    ...campaigns
                      .map((c) => ({
                        value: c._id,
                        label: `${c.orderNumberSequencePrefix} - ${c.name} - ${c.place}`,
                      }))
                      .filter((c) => c.value === initialValues.campaignId),
                  ]}
                  disabled
                />
              ) : null}

              <Button
                type="submit"
                text="Update Customer"
                icon={<EditIcon />}
              />
            </Form>
          )}
        </Formik>
        <Loading isLoading={isSaving} variant="overlay" />
      </Section>
    </div>
  );
}

export default AddCustomerPage;
